import {
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  FormattedDateTime,
  Icon,
  InlineStack,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Stack,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import type { DeliveryDetail } from '@/services/db/delivery/entity';
import { buildInAppConfig, type ConfigRouteRule } from '../utils/in-app-config';

type InAppConfigProps = { detail: DeliveryDetail };

/**
 * The In-App Config panel — priority, display, expiration, and route rules —
 * ported from the Ember `cio-delivery-detail/in-app-config` component. In the
 * Ember layout this lives in the right-hand aside; the Hydra shell (FPS-2592)
 * ships a single column, so it renders as a panel in the main flow.
 */
export function InAppConfig({ detail }: InAppConfigProps) {
  const config = buildInAppConfig(detail);

  return (
    <Panel data-test-in-app-config>
      <PanelHeader>
        <PanelTitle>In-App Config</PanelTitle>
      </PanelHeader>

      <PanelSection>
        <DescriptionList layout="horizontal">
          <DescriptionListTerm text="product-p-secondary" color="subtle">
            Priority
          </DescriptionListTerm>
          <DescriptionListDetails data-test-in-app-priority>{config.priority}</DescriptionListDetails>

          <DescriptionListTerm text="product-p-secondary" color="subtle">
            Display
          </DescriptionListTerm>
          <DescriptionListDetails data-test-in-app-display>{config.display}</DescriptionListDetails>

          {config.expiration != null ? (
            <>
              <DescriptionListTerm text="product-p-secondary" color="subtle">
                Expiration
              </DescriptionListTerm>
              <DescriptionListDetails data-test-in-app-expiration>
                <FormattedDateTime
                  date={config.expiration}
                  format="medium_with_time"
                  withRelativeDateFromNow
                  withTooltip
                />
              </DescriptionListDetails>
            </>
          ) : null}

          {config.included.length > 0 ? <RouteRuleSection label="Included" rules={config.included} /> : null}
          {config.excluded.length > 0 ? <RouteRuleSection label="Excluded" rules={config.excluded} /> : null}
          {config.showLegacy ? <RouteRuleSection label="Page Rules" rules={config.legacy} /> : null}
        </DescriptionList>
      </PanelSection>
    </Panel>
  );
}

type RouteRuleSectionProps = { label: string; rules: ConfigRouteRule[] };

/** A term + a stack of route-rule rows (Ember's Included / Excluded / Page Rules blocks). */
function RouteRuleSection({ label, rules }: RouteRuleSectionProps) {
  return (
    <>
      <DescriptionListTerm text="product-p-secondary" color="subtle">
        {label}
      </DescriptionListTerm>
      <DescriptionListDetails>
        <Stack>
          {rules.map((rule, index) => (
            <RouteRuleRow key={`${rule.platform}-${rule.operator}-${rule.value}-${index}`} rule={rule} />
          ))}
        </Stack>
      </DescriptionListDetails>
    </>
  );
}

type RouteRuleRowProps = { rule: ConfigRouteRule };

/** One route rule: platform icon, operator, and the (truncated) match value (Ember `RouteRuleDetails`). */
function RouteRuleRow({ rule }: RouteRuleRowProps) {
  return (
    <InlineStack gap="050" alignBlock="center">
      <Icon name={rule.icon} size="sm" />
      <Text size="s">{rule.operator}</Text>
      <Truncated weight="semibold" size="s" style={{ maxWidth: '200px' }}>
        {rule.value}
      </Truncated>
    </InlineStack>
  );
}
